import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { entityConfigApi } from '../../services/entityConfigApi';
import { EntityConfig } from '../../types';
import { Plus, Edit, Trash2, Save, X, Users } from 'lucide-react';
import Layout from '../../components/Layout';

const EntityConfigsPage: React.FC = () => {
    const { t, isRTL } = useI18n();
    const [configs, setConfigs] = useState<EntityConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentConfig, setCurrentConfig] = useState<Partial<EntityConfig>>({});
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            const data = await entityConfigApi.getAllConfigs();
            setConfigs(data.content);
        } catch (err) {
            setError('Failed to fetch entity configurations');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setCurrentConfig({
            name: '',
            maxCompanies: 1,
            settings: {}
        });
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleEdit = (config: EntityConfig) => {
        setCurrentConfig(config);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this configuration?')) {
            try {
                await entityConfigApi.deleteConfig(id);
                fetchConfigs();
            } catch (err) {
                setError('Failed to delete configuration');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isEditing && currentConfig.id) {
                await entityConfigApi.updateConfig(currentConfig.id, currentConfig);
            } else {
                await entityConfigApi.createConfig(currentConfig);
            }
            setIsModalOpen(false);
            fetchConfigs();
        } catch (err: any) {
            // Extract error message from response if available
            const errorMessage = err.response?.data?.message || err.message || 'Failed to save configuration';
            setError(errorMessage);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Entity Configurations</h1>
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={20} />
                        Create Config
                    </button>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
                        <span className="block sm:inline">{error}</span>
                        <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
                            <X size={18} className="cursor-pointer" />
                        </span>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="p-4 font-semibold text-gray-600">Name</th>
                                <th className="p-4 font-semibold text-gray-600">Entity ID</th>
                                <th className="p-4 font-semibold text-gray-600">Max Companies</th>
                                <th className="p-4 font-semibold text-gray-600">Package ID</th>
                                <th className="p-4 font-semibold text-gray-600">Subscription End</th>
                                <th className="p-4 font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {configs.map((config) => (
                                <tr key={config.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4">{config.name}</td>
                                    <td className="p-4">{config.entityId}</td>
                                    <td className="p-4">{config.maxCompanies}</td>
                                    <td className="p-4">{config.packageId || '-'}</td>
                                    <td className="p-4">
                                        {config.subscriptionEndDate ? new Date(config.subscriptionEndDate).toLocaleDateString() : 'Lifetime'}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => window.location.href = `/admin/entity-configs/${config.entityId}/subscribers`}
                                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                                                title="Manage Subscribers"
                                            >
                                                <Users size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(config)}
                                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                                title="Edit"
                                            >
                                                <Edit size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(config.id)}
                                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                                title="Delete"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {configs.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-gray-500">
                                        No configurations found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {isEditing ? 'Edit Configuration' : 'New Configuration'}
                                </h2>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentConfig.name || ''}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>



                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Companies</label>
                                    <input
                                        type="number"
                                        min="1"
                                        required
                                        value={currentConfig.maxCompanies || 1}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, maxCompanies: parseInt(e.target.value) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Package ID (Optional)</label>
                                    <input
                                        type="text"
                                        value={currentConfig.packageId || ''}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, packageId: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Subscription End Date</label>
                                    <input
                                        type="date"
                                        value={currentConfig.subscriptionEndDate ? new Date(currentConfig.subscriptionEndDate).toISOString().split('T')[0] : ''}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, subscriptionEndDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Leave empty for lifetime access.</p>
                                </div>

                                <div className="flex items-center gap-2 mt-4">
                                    <input
                                        type="checkbox"
                                        id="stockFeatureEnabled"
                                        checked={currentConfig.stockFeatureEnabled || false}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, stockFeatureEnabled: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="stockFeatureEnabled" className="text-sm font-medium text-gray-700">
                                        Enable Stock Features (Orders, Movements)
                                    </label>
                                </div>

                                <div className="flex items-center gap-2 mt-2">
                                    <input
                                        type="checkbox"
                                        id="customTemplatesEnabled"
                                        checked={currentConfig.customTemplatesEnabled || false}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, customTemplatesEnabled: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="customTemplatesEnabled" className="text-sm font-medium text-gray-700">
                                        Enable Custom Templates
                                    </label>
                                </div>

                                <div className="flex items-center gap-2 mt-2">
                                    <input
                                        type="checkbox"
                                        id="encryptionEnabled"
                                        checked={currentConfig.encryptionEnabled || false}
                                        onChange={(e) => setCurrentConfig({ ...currentConfig, encryptionEnabled: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label htmlFor="encryptionEnabled" className="text-sm font-medium text-gray-700">
                                        Enable Encryption Features
                                    </label>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <Save size={18} />
                                        Save Configuration
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
                }
            </div >
        </Layout >
    );
};

export default EntityConfigsPage;
